import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Ticket as TicketIcon, EnvelopeSimple, LinkSimple, Warning } from "@phosphor-icons/react";
import { timeAgo, timeUntil, initials } from "@/lib/format";
import { PortalKeyDate, PortalApprovalStatus, PortalChecklist } from "@/components/PortalTicketReadOnly";
import { PortalDeferredDetails } from "@/components/PortalDeferredDetails";
import PortalSubmissionsGrouped from "@/components/PortalSubmissionsGrouped";
import PortalShell from "@/components/PortalShell";
import NewStartsCalendar from "@/pages/NewStartsCalendar";

/**
 * Small "who's on it" chip for the requester portal. Renders the
 * assignee's initials/avatar + name, or a muted "Awaiting assignment"
 * placeholder when nobody's picked it up. Portal-safe: consumes the
 * scrubbed `{name, avatar_url}` payload — no user id / email is
 * exposed to the requester.
 */
function AssigneePill({ assignee }) {
  if (!assignee || !assignee.name) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-[11px] text-slate-500 font-mono"
        data-testid="portal-assignee-unassigned"
      >
        <span className="w-5 h-5 rounded-full bg-slate-100 border border-slate-200" />
        Awaiting assignment
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs text-slate-700"
      data-testid="portal-assignee-pill"
    >
      {assignee.avatar_url ? (
        <img
          src={assignee.avatar_url}
          alt=""
          className="w-5 h-5 rounded-full object-cover border border-slate-200"
        />
      ) : (
        <span className="w-5 h-5 rounded-full bg-[#4f4c90]/10 text-[#4f4c90] text-[10px] font-mono flex items-center justify-center border border-[#4f4c90]/20">
          {initials(assignee.name)}
        </span>
      )}
      <span className="text-slate-700">{assignee.name}</span>
    </span>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const portalApi = axios.create({ baseURL: API });
portalApi.interceptors.request.use((c) => {
  const t = localStorage.getItem("hd_portal_token");
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

export function PortalLogin() {
  const [email, setEmail] = useState("");
  const [magicLink, setMagicLink] = useState("");
  const [sentToInbox, setSentToInbox] = useState(false);
  const [notFound, setNotFound] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const request = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNotFound("");
    try {
      const r = await axios.post(`${API}/portal/request`, { email });
      if (r.data.token) {
        // Dev / preview fallback — Graph not configured on server, magic
        // token comes back in-body so we render the "click to continue"
        // button below.
        setMagicLink(r.data.magic_link);
      } else {
        // Production path — email was sent, don't reveal the token.
        setSentToInbox(true);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      // 404 = no tickets or submissions for this email. Surface it
      // clearly as an inline banner instead of a toast so the user
      // understands why no email will arrive.
      if (err?.response?.status === 404) {
        setNotFound(detail || "We couldn't find any tickets or form submissions for that email address.");
      } else {
        toast.error(detail || "Something went wrong — please try again.");
      }
    } finally { setLoading(false); }
  };

  const useLink = async () => {
    const token = new URL(magicLink, window.location.origin).searchParams.get("token");
    const r = await axios.post(`${API}/portal/verify?token=${token}`);
    localStorage.setItem("hd_portal_token", r.data.token);
    localStorage.setItem("hd_portal_email", r.data.email);
    navigate("/portal/tickets");
  };

  return (
    <div className="grain min-h-screen w-full flex items-center justify-center p-6" style={{ background: "#FFFFFF" }}>
      <div className="w-full max-w-md space-y-5">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-[#4f4c90] flex items-center justify-center">
            <TicketIcon size={20} weight="duotone" color="white" />
          </div>
          <div>
            <div className="font-heading font-bold text-lg">Support portal</div>
            <div className="text-[11px] text-slate-500 font-mono">track your tickets</div>
          </div>
        </div>

        {!magicLink && !sentToInbox ? (
          <form onSubmit={request} className="hairline rounded-lg bg-white p-5 space-y-3">
            <div>
              <h1 className="font-heading text-xl font-bold tracking-tight">Sign in to view your tickets</h1>
              <p className="text-sm text-slate-500 mt-1">Enter your work email. We&apos;ll email you a magic link (no password).</p>
            </div>
            {notFound && (
              <div
                data-testid="portal-not-found-banner"
                className="rounded-md border border-amber-300 bg-amber-50 p-3 text-[13px] text-amber-900 flex items-start gap-2"
              >
                <Warning size={16} weight="duotone" className="text-amber-600 mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <div className="font-medium">We couldn&apos;t find you yet.</div>
                  <div className="text-amber-800/90">{notFound}</div>
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs text-slate-700">Work email</Label>
              <Input
                data-testid="portal-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 bg-[#FFFFFF] border-slate-200 h-10"
                placeholder="jane@company.com"
              />
            </div>
            <Button
              data-testid="portal-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#4f4c90] hover:bg-[#5F5CA5] h-10"
            >
              {loading ? "Sending…" : "Send magic link →"}
            </Button>
          </form>
        ) : sentToInbox ? (
          <div className="hairline rounded-lg bg-white p-5 space-y-3" data-testid="portal-sent-to-inbox">
            <div className="flex items-center gap-2 text-sm text-emerald-700">
              <EnvelopeSimple size={18} weight="duotone" /> Check your inbox
            </div>
            <p className="text-sm text-slate-700">
              We&apos;ve sent a sign-in link to <b>{email}</b>. Click it to open your ticket portal — the link expires in 15 minutes.
            </p>
            <p className="text-xs text-slate-500">
              Didn&apos;t receive it? Check your spam folder, or {" "}
              <button
                type="button"
                onClick={() => setSentToInbox(false)}
                className="text-[#4f4c90] hover:underline"
              >try again</button>.
            </p>
          </div>
        ) : (
          <div className="hairline rounded-lg bg-white p-5 space-y-3">
            <div className="flex items-center gap-2 text-sm text-[#8B87C8]">
              <EnvelopeSimple size={18} weight="duotone" /> Magic link generated
            </div>
            <p className="text-xs text-slate-500">
              Dev mode — email sending is disabled, so click below to continue.
            </p>
            <div className="bg-[#FFFFFF] border border-slate-200 rounded-md p-3 text-[11px] font-mono text-slate-600 break-all">
              {magicLink}
            </div>
            <Button data-testid="portal-use-link" onClick={useLink} className="w-full bg-[#4f4c90] hover:bg-[#5F5CA5] h-10 gap-1">
              <LinkSimple size={14} /> Continue with this link
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export function PortalTickets() {
  const [tickets, setTickets] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    portalApi.get("/portal/tickets").then((r) => setTickets(r.data))
      .catch(() => { localStorage.removeItem("hd_portal_token"); navigate("/portal"); });
  }, [navigate]);

  return (
    <PortalShell>
      <div className="max-w-3xl space-y-5">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">My tickets</h1>
          <div className="text-sm text-slate-500 mt-1">Every ticket you&apos;ve raised — click one to see the thread.</div>
        </div>

        <div className="hairline rounded-lg bg-white overflow-hidden">
          {tickets.length === 0 && <div className="p-10 text-center text-slate-500 text-sm">No tickets yet</div>}
          {tickets.map((t) => {
            const sla = timeUntil(t.sla_resolution_due);
            return (
              <button key={t.id} onClick={() => navigate(`/portal/tickets/${t.id}`)}
                data-testid="portal-ticket-row"
                className="w-full text-left px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 flex items-center gap-3">
                <span className="font-mono text-xs text-slate-500">#{t.number}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{t.subject}</div>
                  <div className="text-[11px] text-slate-500 font-mono flex items-center gap-2 mt-0.5">
                    <span>{timeAgo(t.created_at)}</span>
                    <span className="text-slate-300">·</span>
                    <AssigneePill assignee={t.assignee} />
                  </div>
                </div>
                <span className={`chip status-${t.status}`}>{t.status.replace("_"," ")}</span>
                <span className={`chip prio-${t.priority}`}>{t.priority}</span>
              </button>
            );
          })}
        </div>
      </div>
    </PortalShell>
  );
}

export function PortalTicketDetail() {
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();
  const id = window.location.pathname.split("/").pop();

  const load = () => portalApi.get(`/portal/tickets/${id}`).then((r) => setData(r.data))
    .catch(() => { toast.error("Ticket not found"); navigate("/portal/tickets"); });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await portalApi.post(`/portal/tickets/${id}/comments`, { body: reply });
      setReply("");
      load();
      toast.success("Reply sent");
    } catch { toast.error("Failed"); }
    finally { setSending(false); }
  };

  if (!data) return (
    <PortalShell>
      <div className="text-slate-500 text-sm">Loading…</div>
    </PortalShell>
  );
  const { ticket, assignee, comments, checklist_items, submission } = data;

  return (
    <PortalShell>
      <div className="max-w-3xl space-y-4">
        <button onClick={() => navigate("/portal/tickets")} className="text-xs text-slate-500 hover:text-slate-900 font-mono">← My tickets</button>
        <div>
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-mono text-sm text-slate-500">#{ticket.number}</span>
            <h1 className="font-heading text-2xl font-bold tracking-tight">{ticket.subject}</h1>
          </div>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className={`chip status-${ticket.status}`}>{ticket.status.replace("_"," ")}</span>
            <span className={`chip prio-${ticket.priority}`}>{ticket.priority}</span>
            {ticket.awaiting_details && (
              <span className="chip !bg-amber-50 !text-amber-800 !border-amber-200" data-testid="portal-awaiting-badge">Awaiting details</span>
            )}
            <span className="text-xs text-slate-500 font-mono">Updated {timeAgo(ticket.updated_at)}</span>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">Handled by</span>
            <AssigneePill assignee={assignee} />
          </div>
        </div>

        <div className="hairline rounded-lg bg-white p-4">
          <div className="text-[11px] text-slate-500 font-mono uppercase tracking-wider">Original request</div>
          <div className="text-sm text-slate-800 whitespace-pre-wrap mt-2">{ticket.description}</div>
        </div>

        <PortalKeyDate ticket={ticket} />
        <PortalApprovalStatus ticket={ticket} />
        <PortalDeferredDetails
          submission={submission}
          ticketId={id}
          apiClient={portalApi}
          onSaved={load}
        />
        <PortalChecklist items={checklist_items} />

        {comments.map((c) => (
          <div key={c.id} className="hairline rounded-lg bg-white p-4">
            <div className="text-sm font-medium">{c.author_name}</div>
            <div className="text-[11px] text-slate-500 font-mono">{timeAgo(c.created_at)}</div>
            <div className="text-sm text-slate-800 whitespace-pre-wrap mt-2">{c.body}</div>
          </div>
        ))}

        <div className="hairline rounded-lg bg-white p-4">
          <div className="text-[11px] text-slate-500 font-mono uppercase tracking-wider mb-2">Add a reply</div>
          <textarea data-testid="portal-reply-box" value={reply} onChange={(e) => setReply(e.target.value)}
            placeholder="Type your reply…"
            className="w-full bg-[#FFFFFF] border border-slate-200 rounded-md p-2 text-sm min-h-24 outline-none focus:ring-1 focus:ring-[#4f4c90]" />
          <div className="flex justify-end mt-2">
            <Button data-testid="portal-reply-submit" onClick={send} disabled={sending || !reply.trim()} className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-8">
              {sending ? "Sending…" : "Send reply"}
            </Button>
          </div>
        </div>
      </div>
    </PortalShell>
  );
}

/**
 * Feature B — "Form submissions" page inside the portal. Reuses the
 * shared `SubmissionsList` component (same one the admin form editor
 * uses), pointing at `/portal/submissions` which returns only this
 * portal user's own submissions across every form.
 */
export function PortalMySubmissions() {
  return (
    <PortalShell>
      <div className="max-w-3xl space-y-5">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Form submissions</h1>
          <div className="text-sm text-slate-500 mt-1">Grouped by form. Click a submission to open its ticket.</div>
        </div>

        <PortalSubmissionsGrouped
          apiClient={portalApi}
          fetchUrl="/portal/submissions"
          ticketLinkPrefix="/portal/tickets"
        />
      </div>
    </PortalShell>
  );
}

/**
 * New starts calendar for the portal — reuses the shared
 * NewStartsCalendar component in read-only mode, pointed at
 * /api/portal/new-starts which returns only the onboarding tickets
 * this portal user personally submitted the form for.
 */
export function PortalNewStarts() {
  return (
    <PortalShell>
      <NewStartsCalendar
        apiClient={portalApi}
        fetchUrl="/portal/new-starts"
        ticketLinkPrefix="/portal/tickets"
        title="New starts"
        subtitle="Onboarding forms you and your portal team have submitted, plotted against their start date. Click a name to open the ticket."
      />
    </PortalShell>
  );
}

/**
 * PortalSubmitForm — a picker for every active public form, exposed
 * inside the portal so requesters can raise new tickets without hunting
 * for a form URL. Puts "Recently submitted" at the top (personalised)
 * and "All forms" below.
 *
 * Public forms live at /f/{slug}, so we open that route in a new tab —
 * the public form flow already handles anonymous submission end-to-end.
 */
export function PortalSubmitForm() {
  const [forms, setForms] = useState(null);

  useEffect(() => {
    portalApi.get("/portal/forms")
      .then((r) => setForms(r.data || []))
      .catch(() => setForms([]));
  }, []);

  const recent = (forms || [])
    .filter((f) => f.last_submitted_at)
    .sort((a, b) => (a.last_submitted_at > b.last_submitted_at ? -1 : 1));
  const all = (forms || []).slice().sort((a, b) => a.title.localeCompare(b.title));

  const Card = ({ f, testId }) => (
    <a
      href={`/forms/${f.slug}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className="hairline rounded-lg bg-white p-4 flex flex-col gap-2 hover:border-[#4f4c90]/40 hover:shadow-sm transition-all group"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium text-sm text-slate-900 group-hover:text-[#4f4c90] transition-colors truncate">{f.title}</div>
          {f.issue_type && (
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mt-0.5">{f.issue_type}</div>
          )}
        </div>
      </div>
      {f.description && (
        <div className="text-[12px] text-slate-600 line-clamp-3">{f.description}</div>
      )}
      <div className="text-[11px] text-[#4f4c90] font-medium mt-auto pt-1">
        Open form →
      </div>
    </a>
  );

  return (
    <PortalShell>
      <div className="max-w-4xl space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Submit a form</h1>
          <div className="text-sm text-slate-500 mt-1">Pick a form to raise a new request.</div>
        </div>

        {forms === null && <div className="text-xs text-slate-500 italic">Loading forms…</div>}

        {forms !== null && forms.length === 0 && (
          <div className="hairline rounded-lg bg-white p-8 text-center text-sm text-slate-500" data-testid="portal-submit-empty">
            No active forms to submit right now — please contact IT support.
          </div>
        )}

        {recent.length > 0 && (
          <section data-testid="portal-submit-recent">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-3">Recently submitted</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {recent.slice(0, 4).map((f) => (
                <Card key={f.id} f={f} testId={`portal-submit-recent-${f.id}`} />
              ))}
            </div>
          </section>
        )}

        {forms !== null && forms.length > 0 && (
          <section data-testid="portal-submit-all">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-3">All forms</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {all.map((f) => (
                <Card key={f.id} f={f} testId={`portal-submit-all-${f.id}`} />
              ))}
            </div>
          </section>
        )}
      </div>
    </PortalShell>
  );
}



/**
 * Handles the `/portal/verify?token=…` link the magic-link email sends
 * users to. Auto-verifies the token, stores the portal JWT, and
 * redirects to `/portal/tickets`. Shows a friendly error state if the
 * token is invalid or expired (past 15 minutes).
 */
export function PortalVerify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("verifying"); // verifying | error
  const [error, setError] = useState("");

  useEffect(() => {
    const token = params.get("token");
    if (!token) { setError("Missing sign-in token."); setState("error"); return; }
    axios.post(`${API}/portal/verify?token=${encodeURIComponent(token)}`)
      .then((r) => {
        localStorage.setItem("hd_portal_token", r.data.token);
        localStorage.setItem("hd_portal_email", r.data.email);
        navigate("/portal/tickets", { replace: true });
      })
      .catch((e) => {
        setError(e?.response?.data?.detail || "This sign-in link is invalid or has expired.");
        setState("error");
      });
  }, [params, navigate]);

  if (state === "verifying") {
    return (
      <div className="min-h-screen grid place-items-center text-slate-500 text-sm" style={{ background: "#FFFFFF" }}>
        Signing you in…
      </div>
    );
  }
  return (
    <div className="min-h-screen grid place-items-center px-4" style={{ background: "#FFFFFF" }}>
      <div className="hairline rounded-lg bg-white p-6 max-w-md text-center">
        <Warning size={28} weight="duotone" className="mx-auto text-amber-500" />
        <div className="mt-2 font-heading font-bold text-lg">Sign-in link unavailable</div>
        <div className="text-sm text-slate-600 mt-1">{error}</div>
        <Button
          onClick={() => navigate("/portal")}
          className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-9 mt-4"
          data-testid="portal-verify-request-new"
        >
          Request a new link
        </Button>
      </div>
    </div>
  );
}
